/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * PasswordResetPage Component
 * ============================================================================
 *
 * Password reset form with new password entry and strength indicator.
 * Includes OTP verification for MFA-enabled accounts.
 *
 * ============================================================================
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Lock, CheckCircle, AlertTriangle, ShieldCheck, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { AuthLayout } from '@/components/auth/AuthLayout';
import { FormInput } from '@/components/ui/FormInput';
import { Button } from '@/components/ui/Button';
import { PasswordStrengthIndicator } from '@/components/ui/PasswordStrengthIndicator';
import { CodeInput } from '@/components/ui/CodeInput';
import {
  useVerifyResetToken,
  useMfaForReset,
  useCompletePasswordReset,
} from '@/hooks/api/useAuth';
import { getErrorMessage, isApiError } from '@/services/api/errors';

// Validation schema factory
const createResetSchema = (t: (key: string) => string) => z.object({
  password: z.string()
    .min(8, t('validation.minLength').replace('{{min}}', '8')),
  confirmPassword: z.string()
    .min(1, t('validation.required')),
}).refine(data => data.password === data.confirmPassword, {
  message: t('validation.passwordMatch'),
  path: ['confirmPassword'],
});

type ResetFormData = z.infer<ReturnType<typeof createResetSchema>>;

export function PasswordResetPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const verifyTokenMutation = useVerifyResetToken();
  const mfaVerifyMutation = useMfaForReset();
  const completeResetMutation = useCompletePasswordReset();

  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [requiresMfa, setRequiresMfa] = useState(false);
  const [mfaVerified, setMfaVerified] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState('');

  // Memoized schema with translations
  const resetSchema = useMemo(() => createResetSchema(t), [t]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ResetFormData>({
    resolver: zodResolver(resetSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const passwordValue = watch('password');
  const confirmPasswordValue = watch('confirmPassword');

  // Validate token on mount
  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = () => {
    if (!token) {
      setTokenError(t('auth.passwordReset.errors.invalidToken'));
      return;
    }

    verifyTokenMutation.mutate(
      { token },
      {
        onSuccess: (data) => {
          if (data.valid) {
            setTokenValid(true);
            setRequiresMfa(data.requiresMfa);
            setMfaVerified(data.mfaVerified);
            setUserEmail(data.email || '');
          } else {
            setTokenError(t('auth.passwordReset.errors.linkExpired'));
          }
        },
        onError: (err) => {
          if (isApiError(err)) {
            if (err.code === 'TOKEN_EXPIRED' || err.code === 'PASSWORD_RESET_TOKEN_EXPIRED') {
              setTokenError(t('auth.passwordReset.errors.linkExpired'));
            } else if (err.code === 'TOKEN_INVALID' || err.code === 'PASSWORD_RESET_TOKEN_INVALID') {
              setTokenError(t('auth.passwordReset.errors.invalidToken'));
            } else {
              setTokenError(err.message || getErrorMessage(err.code));
            }
          } else {
            setTokenError(t('auth.passwordReset.errors.validationFailed'));
          }
        },
      }
    );
  };

  const handleMfaVerify = (code: string) => {
    if (!token) return;

    setMfaError(null);

    mfaVerifyMutation.mutate(
      { token, code },
      {
        onSuccess: (data) => {
          if (data.verified) {
            setMfaVerified(true);
          } else {
            setMfaError(t('auth.mfa.invalidCode'));
            setMfaCode('');
          }
        },
        onError: (err) => {
          if (isApiError(err)) {
            setMfaError(err.message || getErrorMessage(err.code));
          } else {
            setMfaError(t('auth.mfa.invalidCode'));
          }
          setMfaCode('');
        },
      }
    );
  };

  const onSubmit = (data: ResetFormData) => {
    if (!token) return;

    setSubmitError(null);

    completeResetMutation.mutate(
      { token, newPassword: data.password },
      {
        onSuccess: () => {
          setIsSuccess(true);
        },
        onError: (err) => {
          if (isApiError(err)) {
            if (err.code === 'PASSWORD_REUSED') {
              setSubmitError(t('auth.passwordReset.errors.passwordReused'));
            } else if (err.code === 'PASSWORD_WEAK') {
              setSubmitError(t('auth.passwordReset.errors.passwordWeak'));
            } else {
              setSubmitError(err.message || getErrorMessage(err.code));
            }
          } else {
            setSubmitError(t('notifications.error'));
          }
        },
      }
    );
  };

  // Loading state
  if (verifyTokenMutation.isPending) {
    return (
      <AuthLayout
        title={t('auth.passwordReset.validating.title')}
        subtitle={t('auth.passwordReset.validating.subtitle')}
        showLogo={true}
      >
        <div className="reset-loading">
          <div className="reset-spinner" />
          <p>{t('auth.passwordReset.validating.message')}</p>
        </div>

        <style>{`
          .reset-loading {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 16px;
            padding: 40px 0;
            color: var(--color-text-secondary);
          }

          .reset-spinner {
            width: 48px;
            height: 48px;
            border: 3px solid var(--color-border-default);
            border-top-color: var(--color-brand-primary);
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }

          @keyframes spin {
            to { transform: rotate(360deg); }
          }

          @media (prefers-reduced-motion: reduce) {
            .reset-spinner { animation: none !important; }
          }
        `}</style>
      </AuthLayout>
    );
  }

  // Token error state
  if (tokenError) {
    return (
      <AuthLayout
        title={t('auth.passwordReset.linkInvalid.title')}
        subtitle={t('auth.passwordReset.linkInvalid.subtitle')}
        icon={<AlertTriangle size={28} />}
      >
        <div className="reset-token-error">
          <p>{tokenError}</p>
          <Button
            variant="primary"
            fullWidth
            onClick={() => navigate('/auth/password/request')}
          >
            {t('auth.passwordReset.linkInvalid.requestNew')}
          </Button>
          <Link to="/auth/login" className="reset-back-link">
            <ArrowLeft size={16} />
            {t('auth.mfa.backToLogin')}
          </Link>
        </div>

        <style>{`
          .reset-token-error {
            display: flex;
            flex-direction: column;
            gap: 20px;
            text-align: center;
          }

          .reset-token-error p {
            color: var(--color-text-secondary);
            margin: 0;
          }

          .reset-back-link {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            font-size: var(--text-sm);
            color: var(--color-text-muted);
            text-decoration: none;
          }

          @media (max-width: 640px) {
            .reset-token-error {
              gap: 1rem;
            }
          }
        `}</style>
      </AuthLayout>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <AuthLayout
        title={t('auth.passwordReset.success.title')}
        subtitle={t('auth.passwordReset.success.subtitle')}
        icon={<CheckCircle size={28} />}
      >
        <div className="reset-success">
          <div className="reset-success-icon">
            <CheckCircle size={48} />
          </div>

          <p className="reset-success-message">
            {t('auth.passwordReset.success.message')}
          </p>

          <p className="reset-success-note">
            {t('auth.passwordReset.success.securityNote')}
          </p>

          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={() => navigate('/auth/login')}
            glow
          >
            {t('auth.passwordReset.success.continueLogin')}
          </Button>
        </div>

        <style>{`
          .reset-success {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            text-align: center;
          }

          .reset-success-icon {
            width: 80px;
            height: 80px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: var(--color-success-light);
            color: var(--color-success);
            border-radius: 50%;
          }

          .reset-success-message {
            font-size: var(--text-md);
            color: var(--color-text-secondary);
            margin: 0;
          }

          .reset-success-note {
            font-size: var(--text-sm);
            color: var(--color-text-muted);
            padding: 12px 16px;
            background: var(--color-bg-tertiary);
            border-radius: var(--radius-md);
            margin: 0;
          }

          @media (max-width: 640px) {
            .reset-success {
              gap: 1.25rem;
            }
          }
        `}</style>
      </AuthLayout>
    );
  }

  // MFA verification step
  if (requiresMfa && !mfaVerified) {
    return (
      <AuthLayout
        title={t('auth.passwordReset.mfa.title')}
        subtitle={t('auth.passwordReset.mfa.subtitle')}
        icon={<ShieldCheck size={28} />}
      >
        <div className="reset-mfa">
          <p className="reset-mfa-info">
            {t('auth.passwordReset.mfa.info')}
          </p>

          {mfaError && (
            <div className="reset-mfa-error">
              <AlertTriangle size={16} />
              <span>{mfaError}</span>
            </div>
          )}

          <CodeInput
            length={6}
            onComplete={handleMfaVerify}
            onChange={setMfaCode}
            disabled={mfaVerifyMutation.isPending}
            error={!!mfaError}
          />

          {mfaVerifyMutation.isPending && (
            <p className="reset-mfa-verifying">{t('auth.mfa.verifying')}</p>
          )}

          <Link to="/auth/login" className="reset-back-link">
            <ArrowLeft size={16} />
            {t('auth.passwordReset.mfa.cancelReturn')}
          </Link>
        </div>

        <style>{`
          .reset-mfa {
            display: flex;
            flex-direction: column;
            gap: 24px;
          }

          .reset-mfa-info {
            text-align: center;
            font-size: var(--text-sm);
            color: var(--color-text-secondary);
            margin: 0;
          }

          .reset-mfa-error {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 16px;
            background: var(--color-error-light);
            border: 1px solid var(--color-error);
            border-radius: var(--radius-lg);
            color: var(--color-error);
            font-size: var(--text-sm);
          }

          .reset-back-link {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            font-size: var(--text-sm);
            color: var(--color-text-muted);
            text-decoration: none;
          }

          .reset-mfa-verifying {
            text-align: center;
            font-size: var(--text-sm);
            color: var(--color-text-muted);
            margin: 0;
          }

          @media (max-width: 640px) {
            .reset-mfa {
              gap: 1.25rem;
            }
          }
        `}</style>
      </AuthLayout>
    );
  }

  // Password reset form
  return (
    <AuthLayout
      title={t('auth.passwordReset.resetTitle')}
      subtitle={t('auth.passwordReset.resetSubtitle')}
      icon={<Lock size={28} />}
      iconVariant="teal"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="reset-form">
        {/* Error message */}
        {submitError && (
          <div className="reset-error">
            <AlertTriangle size={16} />
            <span>{submitError}</span>
          </div>
        )}

        {/* New password */}
        <div>
          <FormInput
            label={t('auth.passwordReset.newPassword')}
            type="password"
            autoComplete="new-password"
            leftIcon={<Lock size={18} />}
            error={errors.password?.message}
            {...register('password')}
            value={passwordValue}
          />
          <PasswordStrengthIndicator
            password={passwordValue}
            email={userEmail}
            showRequirements={false}
          />
        </div>

        {/* Confirm password */}
        <FormInput
          label={t('auth.passwordReset.confirmPassword')}
          type="password"
          autoComplete="new-password"
          leftIcon={<Lock size={18} />}
          error={errors.confirmPassword?.message}
          {...register('confirmPassword')}
          value={confirmPasswordValue}
        />

        {/* Submit button */}
        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          loading={completeResetMutation.isPending}
          loadingText={t('auth.passwordReset.resetting')}
          glow
        >
          {t('auth.passwordReset.resetButton')}
        </Button>
      </form>

      <style>{`
        .reset-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .reset-error {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          background: var(--color-error-light);
          border: 1px solid var(--color-error);
          border-radius: var(--radius-lg);
          color: var(--color-error);
          font-size: var(--text-sm);
        }

        .reset-back-link {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          font-size: var(--text-sm);
          color: var(--color-text-muted);
          text-decoration: none;
          transition: color 0.2s ease;
        }

        .reset-back-link:hover {
          color: var(--color-text-primary);
        }

        @media (max-width: 640px) {
          .reset-form {
            gap: 1rem;
          }
        }
      `}</style>
    </AuthLayout>
  );
}

export default PasswordResetPage;
